import { I18n } from "@assets/strings";
import React from "react";
import styled from "styled-components";
import { ImageVersions } from "@domain/media/dtos";
import { Media } from "@domain/media/media";
import { formatBytes } from "@domain/media/utils";
import { useModalTools } from "@ui/modals/modalContext";
import { BackgroundImage } from "@ui/components/backgroundImage";
import { Clamp } from "@ui/components/containers/clamp";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { AudioIcon, CameraIcon, EyeIcon, FileIcon, ImageIcon, PdfIcon } from "@ui/components/icons";
import { row, Stack } from "@ui/components/layout";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { MenuRow } from "@ui/components/menus/menuRow";
import { IconWrapper, SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { TextSkeleton } from "@ui/components/skeleton/basicSkeletons";
import { AssetEditModal } from "./assetEditModal";

interface AssetViewProps extends React.HTMLAttributes<HTMLDivElement> {
	asset: Media;
	onDelete?: () => void;
	editable?: boolean;
}

export const AssetView: React.FC<AssetViewProps> = ({ asset, onDelete, onClick, editable = true, ...htmlProps }) => {
	const mimeType = asset.mimeType;
	const { createModal } = useModalTools();

	return (
		<Container {...htmlProps}>
			<Content data-asset="true" onClick={onClick}>
				<TopSide>
					{mimeType.startsWith("image") ? (
						<BackgroundImage image={asset} size={ImageVersions.SMALL}>
							<AssetIconWrapper>
								<ImageIcon />
							</AssetIconWrapper>
						</BackgroundImage>
					) : (
						<Stack gap={0} align="center" justify="flex-end">
							<BigIconWrapper>
								{mimeType.startsWith("video") ? (
									<CameraIcon />
								) : mimeType.startsWith("audio") ? (
									<AudioIcon />
								) : mimeType === "application/pdf" ? (
									<PdfIcon />
								) : (
									<FileIcon />
								)}
							</BigIconWrapper>
						</Stack>
					)}
				</TopSide>
				<BottomSide size={1}>{asset.displayName}</BottomSide>
				{asset.size && <Size>{formatBytes(asset.size)}</Size>}
			</Content>
			<AssetMenu blur closeOnSelect>
				<SimpleMenuContent
					onEdit={editable ? () => createModal((close) => <AssetEditModal asset={asset} close={close} />) : undefined}
					editLabel={I18n.t("rename")}
					onDelete={onDelete}
				>
					<MenuRow onClick={() => window.open(asset.url, "_blank")}>
						<IconWrapper>
							<EyeIcon />
						</IconWrapper>
						<span>{I18n.t("asset.open")}</span>
					</MenuRow>
				</SimpleMenuContent>
			</AssetMenu>
		</Container>
	);
};

export const AssetViewSkeleton: React.FC<React.HTMLAttributes<HTMLDivElement>> = (htmlProps) => {
	return (
		<Container {...htmlProps}>
			<TopSide>
				<RelativeSkeleton>
					<AssetIconWrapper />
				</RelativeSkeleton>
			</TopSide>
			<BottomSide />
			<Size>
				<TextSkeleton />
			</Size>
		</Container>
	);
};

const Container = styled.div`
	position: relative;
`;

const Content = styled(RoundedBlock)`
	overflow: hidden;
	position: relative;
`;
const TopSide = styled.div`
	height: 90px;

	> div {
		height: 100%;
	}
`;

const BottomSide = styled(Clamp)`
	padding: 30px 10px 0;
	word-break: break-all;
	${({ theme }) => theme.textStyles.text};
	text-align: center;
	margin-bottom: 3px;
`;

const Size = styled.div`
	${({ theme }) => theme.textStyles.small};
	color: ${({ theme }) => theme.colors.textSecondary};
	margin-bottom: 10px;
	padding-left: 10px;
`;

const AssetIconWrapper = styled.div`
	position: absolute;
	bottom: 0;
	left: 50%;
	transform: translate(-50%, 50%);
	width: 30px;
	height: 30px;
	border-radius: 15px;
	${row(0, "center", "center")};
	background-color: ${({ theme }) => theme.colors.secondaryLight};
	svg {
		width: 13px;
		path {
			fill: ${({ theme }) => theme.colors.secondary};
		}
	}
`;

const BigIconWrapper = styled.div`
	width: 50px;
	height: 50px;
	border-radius: 25px;
	${row(0, "center", "center")};
	background-color: ${({ theme }) => theme.colors.secondaryLight};
	svg {
		width: 23px;
		height: 23px;
		path {
			fill: ${({ theme }) => theme.colors.secondary};
		}
	}
`;

const RelativeSkeleton = styled.div`
	position: relative;
	background-color: ${({ theme }) => theme.colors.bodyBackground};
`;

const AssetMenu = styled(KebabMenu)`
	position: absolute;
	top: 5px;
	right: 5px;
`;
